/**
 * AdminActivity — live activity feed.
 *
 * Last 100 events from db.user_events, auto-refreshing every 10s.
 * Filterable by event_type so you can isolate "show me only signups"
 * or "show me only score_shown". This is the page you leave open in a
 * tab while traffic is happening — it's the pulse of the app.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { Activity, Filter, Loader2, Pause, Play, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/apiBase';
import useHideCookieBanner from '../utils/useHideCookieBanner';
import AdminReadinessBanner from '../components/AdminReadinessBanner';
import AdminSubNav from '../components/AdminSubNav';


// Color-code common event groups for at-a-glance scanning.
const eventTone = (et) => {
  const e = (et || '').toLowerCase();
  if (e.includes('signup') || e.includes('register')) return { fg: '#065f46', bg: '#ecfdf5' };
  if (e.includes('checkout') || e.includes('purchase')) return { fg: '#1e40af', bg: '#dbeafe' };
  if (e.includes('block') || e.includes('error') || e.includes('fail')) return { fg: '#991b1b', bg: '#fef2f2' };
  if (e.includes('unlock') || e.includes('score_shown')) return { fg: '#7c2d12', bg: '#fef3c7' };
  if (e.includes('funnel') || e.includes('scroll')) return { fg: '#4b5563', bg: '#f3f4f6' };
  return { fg: '#0f1115', bg: '#e5e7eb' };
};


const ageString = (iso) => {
  if (!iso) return '';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch (_e) {
    return iso;
  }
};


const AdminActivity = () => {
  const { user, loading: authLoading } = useAuth();
  useHideCookieBanner();
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState('');
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (filter.trim()) params.set('event_type', filter.trim());
      const r = await axios.get(`${API}/admin/activity?${params}`, { withCredentials: true });
      setEvents(r.data?.events || []);
    } catch (_e) {
      // soft-fail
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
    if (paused) return undefined;
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [load, paused]);

  if (authLoading) return <div className="pt-24 text-center text-[#6b7280]">Loading…</div>;
  if (!user || user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20 pb-12" data-testid="admin-activity-page">
      <AdminReadinessBanner />
      <AdminSubNav />

      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7]">ADMIN · ACTIVITY</span>
            <h1 className="font-satoshi text-3xl font-black text-[#0f1115] flex items-center gap-2">
              <Activity size={22} className="text-[#10b981] animate-pulse" />
              Live Activity Feed
            </h1>
            <p className="text-sm text-[#6b7280]">Last 100 events · refreshes every 10s</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 bg-white border border-[#e5e7eb] px-3 py-1.5">
              <Filter size={14} className="text-[#6b7280]" />
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="filter by event_type (e.g. signup)"
                className="text-sm outline-none w-56"
                data-testid="activity-filter"
              />
            </div>
            <button
              onClick={() => setPaused((p) => !p)}
              className="px-3 py-2 text-sm font-semibold bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7] transition-colors inline-flex items-center gap-1.5"
              data-testid="activity-pause"
            >
              {paused ? <><Play size={14} /> Resume</> : <><Pause size={14} /> Pause</>}
            </button>
            <button
              onClick={load}
              className="px-3 py-2 text-sm font-semibold bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7] transition-colors inline-flex items-center gap-1.5"
              data-testid="activity-refresh"
            >
              <RefreshCw size={14} />
              Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="bg-white border border-[#e5e7eb] p-8 text-center">
            <Loader2 size={24} className="animate-spin mx-auto text-[#6b7280]" />
          </div>
        ) : events.length === 0 ? (
          <div className="bg-white border border-[#e5e7eb] p-8 text-center text-[#6b7280]" data-testid="activity-empty">
            No events match {filter ? `"${filter}"` : 'the current filter'}.
          </div>
        ) : (
          <div className="bg-white border border-[#e5e7eb] overflow-x-auto" data-testid="activity-feed">
            <table className="w-full text-sm">
              <thead className="bg-[#f4f5f7] border-b border-[#e5e7eb]">
                <tr className="text-[10px] uppercase tracking-wider text-[#9ca3af]">
                  <th className="text-left py-2 px-3 font-semibold">When</th>
                  <th className="text-left py-2 px-3 font-semibold">Event</th>
                  <th className="text-left py-2 px-3 font-semibold">Funnel</th>
                  <th className="text-left py-2 px-3 font-semibold">User / Visitor</th>
                  <th className="text-right py-2 px-3 font-semibold">Score</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, i) => {
                  const tone = eventTone(e.event_type);
                  return (
                    <tr key={i} className="border-t border-[#e5e7eb] hover:bg-[#f4f5f7]" data-testid={`activity-row-${i}`}>
                      <td className="py-2 px-3 text-[#6b7280] text-xs whitespace-nowrap">{ageString(e.created_at)}</td>
                      <td className="py-2 px-3">
                        <span className="text-[11px] font-bold px-2 py-0.5" style={{ backgroundColor: tone.bg, color: tone.fg }}>
                          {e.event_type}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-xs font-mono text-[#6b7280]">{e.funnel || '—'}</td>
                      <td className="py-2 px-3 text-xs text-[#374151]">
                        {e.email || e.user_id || e.visitor_id || '—'}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums font-semibold">{e.score ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminActivity;
